import Service, { service } from '@ember/service';
import type { ProfileDTO } from 'frontend/changesets/profile';
import { ProfileChangeset } from 'frontend/changesets/profile';
import type ExtendedStoreService from '@triptyk/ember-utils/services/extended-store';
import type FetchService from '@triptyk/ember-utils/services/fetch';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type IntlService from 'ember-intl/services/intl';
import type ApplicationModel from 'frontend/models/application';
import type UserModel from 'frontend/models/user';

export default class ProfileService extends Service {
  @service declare extendedStore: ExtendedStoreService;
  @service declare fetch: FetchService;
  @service declare flashMessages: FlashMessageService;
  @service declare intl: IntlService;

  async changesetProfileFrom(id: string): Promise<ProfileChangeset> {
    const profile = this.extendedStore.peekOrFail('user', id);

    const changeset = new ProfileChangeset({
      id: profile.id,
      kind: profile.kind,
      newsAgreement: profile.newsAgreement,
      applicant: {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        companyNumber: profile.companyNumber ?? '',
        companyName: profile.companyName ?? '',
        institution: profile.institution ?? '',
        cooperationAgreement: profile.cooperationAgreement ?? false,
      },
      gender: profile.gender,
      nationality: profile.nationality,
      professionalSituation: profile.professionalSituation,
      unemployedLength: profile.unemployedLength,
      studyLevel: profile.studyLevel,
      address: {
        streetAndNumber: profile.address?.streetAndNumber ?? '',
        zip: profile.address?.zip ?? '',
        town: profile.address?.town ?? '',
      },
      isBillingAddress: profile.isBillingAddress ?? false,
      billingAddress: {
        streetAndNumber: profile.billingAddress?.streetAndNumber ?? '',
        zip: profile.billingAddress?.zip ?? '',
        town: profile.billingAddress?.town ?? '',
      },
      isCommittee: profile.isCommittee ?? false,
      isTrainingVoucher: profile.isTrainingVoucher ?? false,
      function: profile.function ?? '',
      jobStatus: profile.jobStatus,
      educationFunction: profile.educationFunction,
      faseNumber: profile.faseNumber ?? '',
      typeOfTeaching: profile.typeOfTeaching,
    });

    return changeset;
  }
  changesetProfileFromApplication(
    application: ApplicationModel
  ): ProfileChangeset {
    const changeset = new ProfileChangeset({
      id: application.id,
      kind: application.kind,
      newsAgreement: application.newsAgreement,
      applicant: {
        firstName: application.firstName,
        lastName: application.lastName,
        email: application.email,
        phone: application.phone,
        companyNumber: application.companyNumber ?? '',
        companyName: application.companyName ?? '',
        institution: application.institution ?? '',
        cooperationAgreement: application.cooperationAgreement ?? false,
      },
      gender: application.gender,
      nationality: application.nationality,
      professionalSituation: application.professionalSituation,
      unemployedLength: application.unemployedLength,
      studyLevel: application.studyLevel,
      address: {
        streetAndNumber: application.address?.streetAndNumber ?? '',
        zip: application.address?.zip ?? '',
        town: application.address?.town ?? '',
      },
      isBillingAddress: application.isBillingAddress ?? false,
      billingAddress: {
        streetAndNumber: application.billingAddress?.streetAndNumber ?? '',
        zip: application.billingAddress?.zip ?? '',
        town: application.billingAddress?.town ?? '',
      },
      isCommittee: application.isCommittee ?? false,
      isTrainingVoucher: application.isTrainingVoucher ?? false,
      function: application.function ?? '',
      jobStatus: application.jobStatus,
      educationFunction: application.educationFunction,
      faseNumber: application.faseNumber ?? '',
      typeOfTeaching: application.typeOfTeaching,
    });

    return changeset;
  }
  async saveProfileChangeset(changeset: ProfileChangeset) {
    changeset.execute();
    const changesetData = changeset.data;

    const profile: Record<string, unknown> = {
      id: changesetData.id,
      kind: changesetData.kind,
      newsAgreement: changesetData.newsAgreement,
      firstName: changesetData.applicant.firstName,
      lastName: changesetData.applicant.lastName,
      email: changesetData.applicant.email,
      phone: changesetData.applicant.phone,
      companyNumber: changesetData.applicant.companyNumber,
      companyName: changesetData.applicant.companyName,
      institution: changesetData.applicant.institution,
      cooperationAgreement: changesetData.applicant.cooperationAgreement,
      gender: changesetData.gender,
      nationality: changesetData.nationality,
      professionalSituation: changesetData.professionalSituation,
      unemployedLength: changesetData.unemployedLength,
      studyLevel: changesetData.studyLevel,
      address: {
        streetAndNumber: changesetData.address?.streetAndNumber,
        zip: changesetData.address?.zip,
        town: changesetData.address?.town,
      },
      isBillingAddress: changesetData.isBillingAddress,
      billingAddress: {
        streetAndNumber: changesetData.billingAddress?.streetAndNumber,
        zip: changesetData.billingAddress?.zip,
        town: changesetData.billingAddress?.town,
      },
      isCommittee: changesetData.isCommittee,
      isTrainingVoucher: changesetData.isTrainingVoucher,
      function: changesetData.function,
      jobStatus: changesetData.jobStatus,
      educationFunction: changesetData.educationFunction,
      faseNumber: changesetData.faseNumber,
      typeOfTeaching: changesetData.typeOfTeaching,
    };

    this.setKindInUserStore(changesetData);
    this.setUserData(profile);

    try {
      await this.fetch.request(`users/profile`, {
        method: 'PATCH',
        body: JSON.stringify(profile),
      });
      await changeset.save();
      this.flashMessages.success(this.intl.t('components.pages.profile.save'));
    } catch (e) {
      this.flashMessages.alert(this.intl.t('components.pages.profile.error'));
    }
  }

  private setKindInUserStore(changesetData: ProfileDTO) {
    const profileModel = this.extendedStore.peekOrFail(
      'user',
      changesetData.id as string
    );
    profileModel.setProperties({ kind: changesetData.kind });
  }

  private setUserData(profile: Record<keyof UserModel, unknown>) {
    const profileModel = this.extendedStore.peekOrFail(
      'user',
      profile['id'] as string
    );
    profileModel.setProperties(profile);
  }
}

declare module '@ember/service' {
  interface Registry {
    profile: ProfileService;
  }
}
